import LanguageToggle from "@/components/LanguageToggle";
import { reportError } from "@/utils/reportError";
import { trackLogin } from "@/utils/analytics";
import { fetchIsAdmin } from "@/utils/admin";
import ErrorText from "@/components/ui/ErrorText";
import PrimaryButton from "@/components/ui/PrimaryButton";
import { useAuthForm } from "@/hooks/useAuthForm";
import { supabase } from "@/utils/supabase";
import { showAlert } from "@/utils/showAlert";
import { getAuthRedirectUrl } from "@/utils/authRedirect";
import { translateSupabaseError } from "@/utils/supabaseErrorTranslations";
import { validateEmail, validatePassword } from "@/utils/validationUtils";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

export default function LoginScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  const handleLogin = async (formValues: any) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formValues.email,
        password: formValues.password,
      });

      if (error) {
        // Check if error is due to email not confirmed
        if (error.message.toLowerCase().includes('email not confirmed') || 
            error.message.toLowerCase().includes('email confirmation')) {
          
          showAlert(
            t("login.emailNotConfirmedTitle"),
            t("login.emailNotConfirmedMessage"),
            [
              {
                text: t("common.cancel"),
                style: "cancel"
              },
              {
                text: t("login.resendEmailButton"),
                onPress: async () => {
                  try {
                    const { error: resendError } = await supabase.auth.resend({
                      type: 'signup',
                      email: formValues.email,
                      options: {
                        emailRedirectTo: getAuthRedirectUrl('/confirmation'),
                      },
                    });

                    if (resendError) {
                      showAlert(t("common.errorTitle"), t("login.resendEmailFailedRetry"));
                    } else {
                      showAlert(
                        t("login.emailSentTitle"),
                        t("login.confirmationEmailSentMessage", { email: formValues.email })
                      );
                    }
                  } catch (err) {
                    reportError(err);
                    showAlert(t("common.errorTitle"), t("login.resendEmailFailed"));
                  }
                }
              }
            ]
          );
          return;
        }

        const translatedError = translateSupabaseError(error);
        showAlert(translatedError.title, translatedError.message);
        return;
      }


      if (data.user) {
        trackLogin();
        // Check if user has completed onboarding (notification + ranking consent)
        // AND check if user is soft-deleted
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('display_main_ranking, is_deleted')
          .eq('id', data.user.id)
          .single();

        // Check for soft deleted user first
        if (profileData?.is_deleted) {
          await supabase.auth.signOut();
          showAlert(
            t("login.accountDeletedTitle"),
            t("login.accountDeletedMessage"),
            [{ text: t("common.ok") }]
          );
          return;
        }

        // Accès admin : toujours afficher l'écran d'onboarding à la connexion
        // (prévisualisation QA), avant tout autre routage.
        if (await fetchIsAdmin(data.user.id)) {
          router.replace("/onboarding?loggedIn=1");
          return;
        }

        // If display_main_ranking is null/undefined, user hasn't completed onboarding
        if (!profileError && profileData && profileData.display_main_ranking === null) {
          router.replace("/notification-permission");
        } else {
          router.replace("/(tabs)");
        }
      }
    } catch (error: any) {
      reportError(error);
      const translatedError = translateSupabaseError(error);
      showAlert(translatedError.title, translatedError.message);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      showAlert(t("common.errorTitle"), t("login.resetEmailRequired"));
      return;
    }

    const emailValidation = validateEmail(resetEmail);
    if (!emailValidation.isValid) {
      showAlert(t("common.errorTitle"), emailValidation.error || t("login.emailInvalid"));
      return;
    }

    setIsResetting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: getAuthRedirectUrl("/reset-password"),
      });

      if (error) {
        const translatedError = translateSupabaseError(error);
        showAlert(translatedError.title, translatedError.message);
      } else {
        showAlert(
          t("login.emailSentTitle"),
          t("login.resetEmailSentMessage"),
          [
            {
              text: t("common.ok"),
              onPress: () => {
                setShowForgotPasswordModal(false);
                setResetEmail("");
              },
            },
          ]
        );
      }
    } catch (error: any) {
      reportError(error);
      const translatedError = translateSupabaseError(error);
      showAlert(translatedError.title, translatedError.message);
    } finally {
      setIsResetting(false);
    }
  };

  const form = useAuthForm({
    initialValues: {
      email: "",
      password: "",
    },
    validators: {
      email: validateEmail,
      password: validatePassword,
    },
    onSubmit: handleLogin,
  });

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={styles.container}
    >
      <StatusBar style="dark" />
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.65]}
        style={styles.background}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <LanguageToggle />
          <Text style={styles.title}>{t("login.welcomeTitle")}</Text>

          <View style={styles.formContainer}>
            <View
              style={[
                styles.inputContainer,
                form.touched.email && form.errors.email && styles.inputError,
              ]}
            >
              <TextInput
                placeholder={t("login.emailPlaceholder")}
                placeholderTextColor="#999"
                style={styles.input}
                keyboardType="email-address"
                autoCapitalize="none"
                value={form.values.email as string}
                onChangeText={(text) => form.setFieldValue("email", text)}
                onBlur={() => form.handleBlur("email")}
                editable={!form.isSubmitting}
              />
            </View>
            {form.touched.email && <ErrorText error={form.errors.email} />}
          </View>

          <View style={styles.formContainer}>
            <View
              style={[
                styles.inputContainer,
                form.touched.password &&
                  form.errors.password &&
                  styles.inputError,
              ]}
            >
              <TextInput
                placeholder={t("login.passwordPlaceholder")}
                placeholderTextColor="#999"
                style={[styles.input, { flex: 1 }]}
                secureTextEntry={!showPassword}
                value={form.values.password as string}
                onChangeText={(text) => form.setFieldValue("password", text)}
                onBlur={() => form.handleBlur("password")}
                editable={!form.isSubmitting}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                <Ionicons
                  name={showPassword ? "eye-outline" : "eye-off-outline"}
                  size={20}
                  color="#999"
                />
              </TouchableOpacity>
            </View>
            {form.touched.password && (
              <ErrorText error={form.errors.password} />
            )}
          </View>

          <TouchableOpacity
            style={styles.forgotPassword}
            onPress={() => setShowForgotPasswordModal(true)}
          >
            <Text style={styles.forgotPasswordText}>{t("login.forgotPasswordLink")}</Text>
          </TouchableOpacity>

          <PrimaryButton
            title={
              form.isSubmitting ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                t("login.loginButton")
              )
            }
            onPress={form.handleSubmit}
            disabled={form.isSubmitting}
          />

          <View style={styles.registerContainer}>
            <Text style={styles.registerText}>{t("login.noAccountYet")}</Text>
            <TouchableOpacity onPress={() => router.push("/create-account")}>
              <Text style={styles.registerLink}>{t("login.signUpLink")}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </LinearGradient>

      {/* Forgot Password Modal */}
      <Modal
        visible={showForgotPasswordModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowForgotPasswordModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t("login.forgotPasswordModalTitle")}</Text>
              <TouchableOpacity
                onPress={() => setShowForgotPasswordModal(false)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="close" size={24} color="#000" />
              </TouchableOpacity>
            </View>

            <Text style={styles.modalDescription}>
              {t("login.forgotPasswordModalDescription")}
            </Text>

            <View style={styles.modalInputContainer}>
              <TextInput
                placeholder={t("login.emailPlaceholder")}
                placeholderTextColor="#999"
                style={styles.input}
                keyboardType="email-address"
                autoCapitalize="none"
                value={resetEmail}
                onChangeText={setResetEmail}
                editable={!isResetting}
              />
            </View>

            <TouchableOpacity
              style={styles.modalButton}
              onPress={handleForgotPassword}
              disabled={isResetting}
            >
              <LinearGradient
                colors={["#1C453F", "#78A584"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.modalButtonGradient}
              >
                {isResetting ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  <Text style={styles.modalButtonText}>{t("common.send")}</Text>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 100,
    paddingBottom: 40,
  },
  formContainer: {
    marginBottom: 16,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#000",
    marginBottom: 32,
  },
  inputContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: "transparent",
  },
  inputError: {
    borderColor: "#D32F2F",
  },
  input: {
    flex: 1,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
    height: "100%",
  },
  forgotPassword: {
    alignSelf: "flex-start",
    marginBottom: 24,
  },
  forgotPasswordText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#8B4513",
  },
  registerContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 24,
  },
  registerText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  registerLink: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#8B4513",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContent: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 24,
    width: "100%",
    maxWidth: 400,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
  },
  modalDescription: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    marginBottom: 20,
    lineHeight: 20,
  },
  modalInputContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  modalButton: {
    borderRadius: 12,
    overflow: "hidden",
  },
  modalButtonGradient: {
    paddingVertical: 16,
    alignItems: "center",
  },
  modalButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
});
